import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import routesConfig from 'constants/routesConfig.json';

// components
import ApiLoading from 'shared/ApiLoading';
import ErrorBoundary from 'layout/components/ErrorBoundary';
import { TopBar } from 'topBar/components/TopBar';
import TeamPage from 'teamProfiles/pages/TeamPage';
import UnsavedChangesModal from 'teamProfiles/components/UnsavedChangesModal';
import Tooltip from '@mui/material/Tooltip';
import { General, Arrow } from 'icons/untitledui';

// hooks
import {
  useProfile,
  useUpdateProfileMutation,
  useProfiles,
  useCreateProfileMutation,
  useDepartments,
} from 'teamProfiles/hooks/teamApi';
import useResolveNoteEventId from 'events/hooks/useResolveNoteEventId';
import { useScrolledPast } from 'topBar/hooks/useScrolledPast';
import { Profile } from 'teamProfiles/_types';
import useWindowSize from 'shared/hooks/useWindowResize';
import breakpoints from 'theme/atoms/breakpoints';
import { MultiSelectionDropdown } from 'shared/components/dropdown/MultiSelectionDropdown';
import {
  SelectionDropdown,
  SelectionDropdownOption,
} from 'shared/components/dropdown/SelectionDropdown';

// styles
import * as PageLayout from 'layout/styles/PageLayout.style';
import * as Styled from 'teamProfiles/styles/EditDetails.style';
import * as Text from 'theme/components/Text.style';
import ConditionalWrapper from 'layout/components/ConditionalWrapper';
import { getInitials } from 'teamProfiles/helpers/profileUtils';
import EditableNicknameInput from 'teamProfiles/components/EditableNicknameInput';

// constants
const EMPLOYMENT_STATUS_OPTIONS = ['active', 'inactive'] as const;
type EmploymentStatus = (typeof EMPLOYMENT_STATUS_OPTIONS)[number];

export default function EditDetails(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation('pageTeam');

  const isCreateMode = location.pathname === `${routesConfig.TEAM_PAGE}/new`;
  const searchParams = new URLSearchParams(location.search);
  const prePopulatedName = searchParams.get('full_name') || '';
  const entityExtractionId =
    searchParams.get('entity_extraction_id') || undefined;
  const noteId = searchParams.get('note_id') || undefined;
  const resolveNoteEventId = useResolveNoteEventId();

  // Conditionally load profile data only in edit mode
  const { data: profile, isLoading: isProfileLoading } = useProfile(
    isCreateMode ? undefined : id
  );
  const { data: profiles } = useProfiles();
  const { data: departments } = useDepartments();
  const { mutateAsync: updateProfile, isPending: isUpdateProfilePending } =
    useUpdateProfileMutation(id);
  const { mutateAsync: createProfile, isPending: isCreateProfilePending } =
    useCreateProfileMutation();
  const windowSize = useWindowSize();
  const isDesktopView = windowSize[0] > parseInt(breakpoints.tablet, 10);
  const [formData, setFormData] = useState<
    Omit<Profile, 'id' | 'departments'> & { departments: string[] }
  >({
    full_name: isCreateMode ? prePopulatedName : '',
    nicknames: [],
    employee_id: '',
    employment_status: 'active',
    role: '',
    departments: [],
  });
  const [deletedNicknames, setDeletedNicknames] = useState<Set<string>>(
    new Set()
  );
  const [isAddingNickname, setIsAddingNickname] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const isScrolledPast = useScrolledPast(titleRef);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [pendingNavigationId, setPendingNavigationId] = useState<string | null>(
    null
  );
  const [pendingNavigation, setPendingNavigation] = useState<{
    location: any;
    action: string;
  } | null>(null);
  const [forceReblock, setForceReblock] = useState(0);
  const originalProfileData = useRef<
    (Omit<Profile, 'id' | 'departments'> & { departments: string[] }) | null
  >(null);
  const unblockRef = useRef<(() => void) | null>(null);
  const isHandlingNavigationRef = useRef(false);

  useEffect(() => {
    if (profile && !isCreateMode) {
      const normalized = {
        full_name: profile.full_name ?? '',
        nicknames: profile.nicknames ?? [],
        employee_id: profile.employee_id ?? '',
        employment_status: profile.employment_status ?? 'active',
        role: profile.role ?? '',
        departments: (profile.departments ?? []).map((d) => d.id),
      };
      setFormData(normalized);
      originalProfileData.current = { ...normalized };
      setDeletedNicknames(new Set());
      setIsAddingNickname(false);
    }
  }, [profile, isCreateMode]);

  useEffect(() => {
    let hasChanges = false;

    if (isCreateMode) {
      hasChanges =
        formData.full_name.trim() !== '' ||
        formData.nicknames.length > 0 ||
        formData.employee_id.trim() !== '' ||
        formData.role.trim() !== '' ||
        formData.employment_status !== 'active' ||
        (formData.departments?.length ?? 0) > 0;
    } else if (originalProfileData.current) {
      const original = originalProfileData.current;
      const activeNicknames = formData.nicknames.filter(
        (nickname) => !deletedNicknames.has(nickname)
      );

      const departmentsChanged = (() => {
        const current = formData.departments ?? [];
        const originalDepartments = original.departments ?? [];
        if (current.length !== originalDepartments.length) return true;
        return !current.every((d) => originalDepartments.includes(d));
      })();

      hasChanges =
        formData.full_name !== original.full_name ||
        formData.role !== original.role ||
        formData.employee_id !== original.employee_id ||
        formData.employment_status !== original.employment_status ||
        departmentsChanged ||
        activeNicknames.length !== original.nicknames.length ||
        !activeNicknames.every((nickname) =>
          original.nicknames.includes(nickname)
        );
    }

    if (hasChanges) {
      const unblock = history.block((loc, action) => {
        if (isHandlingNavigationRef.current) {
          return true;
        }

        const isProfileSwitch =
          loc.pathname.startsWith(`${routesConfig.TEAM_PAGE}/`) &&
          loc.pathname.includes('/edit');

        if (isProfileSwitch) {
          return true;
        }

        setPendingNavigation({ location: loc, action });
        setShowUnsavedChangesDialog(true);
        return false;
      });

      unblockRef.current = unblock;
      return () => {
        unblock();
        unblockRef.current = null;
      };
    }
    return undefined;
  }, [history, isCreateMode, formData, deletedNicknames, forceReblock]);

  useEffect(() => {
    if (!isDesktopView && showNameTooltip) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(event.target as Node)
        ) {
          setShowNameTooltip(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [isDesktopView, showNameTooltip]);

  const handleTooltipKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!isDesktopView) {
        setShowNameTooltip(!showNameTooltip);
      }
    }
  };

  const hasUnsavedChanges = () => {
    if (isCreateMode) {
      return (
        formData.full_name.trim() !== '' ||
        formData.nicknames.length > 0 ||
        formData.employee_id.trim() !== '' ||
        formData.role.trim() !== '' ||
        formData.employment_status !== 'active' ||
        (formData.departments?.length ?? 0) > 0
      );
    }

    if (!originalProfileData.current) return false;

    const original = originalProfileData.current;
    const activeNicknames = formData.nicknames.filter(
      (nickname) => !deletedNicknames.has(nickname)
    );

    const departmentsChanged = (() => {
      const current = formData.departments ?? [];
      const originalDepartments = original.departments ?? [];
      if (current.length !== originalDepartments.length) return true;
      return !current.every((d) => originalDepartments.includes(d));
    })();

    return (
      formData.full_name !== original.full_name ||
      formData.role !== original.role ||
      formData.employee_id !== original.employee_id ||
      formData.employment_status !== original.employment_status ||
      departmentsChanged ||
      activeNicknames.length !== original.nicknames.length ||
      !activeNicknames.every((nickname) =>
        original.nicknames.includes(nickname)
      )
    );
  };

  const handleCancel = () => {
    if (isCreateMode) {
      if (isDesktopView) {
        history.replace(routesConfig.TEAM_PAGE);
      } else {
        history.goBack();
      }
      return;
    }
    if (isDesktopView) {
      history.replace(`${routesConfig.TEAM_PAGE}/${id}`);
    } else {
      history.goBack();
    }
  };

  const navigateToProfile = (profileId: string) => {
    if (isDesktopView) {
      history.replace(`${routesConfig.TEAM_PAGE}/${profileId}`);
    } else {
      history.push(`${routesConfig.TEAM_PAGE}/${profileId}`);
    }
  };

  const handleAddProfile = () => {
    history.push(`${routesConfig.TEAM_PAGE}/new`);
  };

  const saveProfile = async () => {
    if (!formData.full_name.trim()) {
      throw new Error('Full name is required');
    }

    if (isCreateMode) {
      const createData = {
        ...formData,
        ...(entityExtractionId && { entity_extraction_id: entityExtractionId }),
      };
      const newProfile = await createProfile(createData);
      return newProfile;
    }

    if (!profile) throw new Error('Profile not found');

    const activeNicknames = formData.nicknames.filter(
      (nickname) => !deletedNicknames.has(nickname)
    );

    await updateProfile({
      ...formData,
      nicknames: activeNicknames,
    });
    return null;
  };

  const handleDone = async () => {
    if (!formData.full_name.trim()) {
      return;
    }

    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    isHandlingNavigationRef.current = true;

    try {
      const newProfile = await saveProfile();

      if (isCreateMode && newProfile) {
        if (noteId) {
          const eventId = await resolveNoteEventId(noteId);
          if (eventId) {
            history.push(`${routesConfig.EVENT_PAGE}/${eventId}`);
          } else {
            history.push(routesConfig.EVENTS_PAGE);
          }
        } else {
          history.push(`${routesConfig.TEAM_PAGE}/${newProfile.id}`);
        }
      } else if (isDesktopView) {
        history.replace(`${routesConfig.TEAM_PAGE}/${id}`);
      } else {
        history.push(`${routesConfig.TEAM_PAGE}/${id}`);
      }
    } catch (error) {
      console.error(
        `Failed to ${isCreateMode ? 'create' : 'update'} profile:`,
        error
      );
      setForceReblock((prev) => prev + 1);
    } finally {
      isHandlingNavigationRef.current = false;
    }
  };

  const handleSaveAndNavigate = async () => {
    isHandlingNavigationRef.current = true;

    const navId = pendingNavigationId;
    const navState = pendingNavigation;

    setPendingNavigationId(null);
    setPendingNavigation(null);
    setShowUnsavedChangesDialog(false);

    if (navId) {
      try {
        await saveProfile();
        if (!isCreateMode) {
          originalProfileData.current = { ...formData };
        }
        navigateToProfile(navId);
      } catch (error) {
        console.error('Failed to save profile changes:', error);
        // eslint-disable-next-line no-alert
        alert('Failed to save changes. Please try again.');
      } finally {
        isHandlingNavigationRef.current = false;
      }
    } else if (navState) {
      try {
        await saveProfile();
        if (!isCreateMode) {
          originalProfileData.current = { ...formData };
        }
        // Unblock navigation and proceed with the original navigation
        if (unblockRef.current) {
          unblockRef.current();
          unblockRef.current = null;
        }
        history.push(navState.location.pathname);
      } catch (error) {
        console.error('Failed to save general navigation changes:', error);
        // eslint-disable-next-line no-alert
        alert('Failed to save changes. Please try again.');
      } finally {
        isHandlingNavigationRef.current = false;
      }
    } else {
      isHandlingNavigationRef.current = false;
    }
  };

  const handleDiscardAndNavigate = () => {
    isHandlingNavigationRef.current = true;

    const navId = pendingNavigationId;
    const navState = pendingNavigation;

    setPendingNavigationId(null);
    setPendingNavigation(null);
    setShowUnsavedChangesDialog(false);

    if (navId) {
      navigateToProfile(navId);
      isHandlingNavigationRef.current = false;
    } else if (navState) {
      if (originalProfileData.current && !isCreateMode) {
        setFormData({ ...originalProfileData.current });
        setDeletedNicknames(new Set());
      } else if (isCreateMode) {
        setFormData({
          full_name: '',
          nicknames: [],
          employee_id: '',
          employment_status: 'active',
          role: '',
          departments: [],
        });
      }
      // Unblock navigation and proceed with the original navigation
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      history.push(navState.location.pathname);
      isHandlingNavigationRef.current = false;
    } else {
      isHandlingNavigationRef.current = false;
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedChangesDialog(false);
    setPendingNavigationId(null);
    setPendingNavigation(null);
  };

  const handleProfileClick = (profileId: string) => {
    if (hasUnsavedChanges()) {
      setPendingNavigationId(profileId);
      setShowUnsavedChangesDialog(true);
    } else {
      navigateToProfile(profileId);
    }
  };

  const handleSaveNewNickname = (value: string) => {
    const trimmedValue = value.trim();

    setIsAddingNickname(false);

    // If value is not empty and not duplicate, add to nicknames
    if (trimmedValue && !formData.nicknames.includes(trimmedValue)) {
      setFormData({
        ...formData,
        nicknames: [...formData.nicknames, trimmedValue],
      });
      // If this nickname was previously deleted (edit mode only), restore it
      if (!isCreateMode && deletedNicknames.has(trimmedValue)) {
        setDeletedNicknames((prev) => {
          const newSet = new Set(prev);
          newSet.delete(trimmedValue);
          return newSet;
        });
      }
    }
  };

  const handleRemoveNickname = (nickname: string) => {
    if (isCreateMode) {
      // In create mode, just remove it immediately
      setFormData({
        ...formData,
        nicknames: formData.nicknames.filter((n) => n !== nickname),
      });
    } else {
      // In edit mode, mark nickname for deletion instead of removing it immediately
      setDeletedNicknames((prev) => new Set(prev).add(nickname));
    }
  };

  const handleRestoreNickname = (nickname: string) => {
    // Remove from deleted set to restore it (edit mode only)
    setDeletedNicknames((prev) => {
      const newSet = new Set(prev);
      newSet.delete(nickname);
      return newSet;
    });
  };

  const getStatusLabel = (status: string) => {
    if (status === 'active') return t('employeeProfile.active');
    if (status === 'inactive') return t('employeeProfile.inactive');
    return status;
  };
  const statusOptions: SelectionDropdownOption[] =
    EMPLOYMENT_STATUS_OPTIONS.map((status) => ({
      value: status,
      label: getStatusLabel(status),
    }));

  const departmentOptions =
    departments
      ?.slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((d) => ({ value: d.id, label: d.name })) ?? [];

  // Show loading only in edit mode when profile is loading
  if (!isCreateMode && isProfileLoading) return <ApiLoading />;
  if (!isCreateMode && !profile)
    return <div>{t('employeeProfile.profileNotFound')}</div>;

  const isPending = isCreateMode
    ? isCreateProfilePending
    : isUpdateProfilePending;
  const translationKey = isCreateMode ? 'addProfile' : 'editDetails';

  return (
    <ErrorBoundary>
      <ConditionalWrapper
        condition={isDesktopView}
        wrapper={(children) => (
          <PageLayout.PageWithSubNav>
            <PageLayout.ColumnSubNav>
              <TeamPage
                profiles={profiles}
                onProfileClick={handleProfileClick}
                isDesktopView={isDesktopView}
                selectedProfileId={isCreateMode ? null : id || null}
                onAddProfileClick={handleAddProfile}
              />
            </PageLayout.ColumnSubNav>
            {children}
          </PageLayout.PageWithSubNav>
        )}
        elseWrapper={(children) => (
          <PageLayout.Page>{children}</PageLayout.Page>
        )}
      >
        <PageLayout.ContentContainer>
          <PageLayout.Content>
            <TopBar
              onBackClick={handleCancel}
              backButtonAriaLabel={
                isCreateMode ? 'Cancel' : t('editDetails.cancel')
              }
              menuItems={[
                {
                  label: t(`${translationKey}.save`),
                  onClick: handleDone,
                  disabled:
                    isPending || (isCreateMode && !formData.full_name.trim()),
                  icon: <General.Check size={20} />,
                  primary: true,
                },
              ]}
              showOnDesktop={isDesktopView}
              title={t(`${translationKey}.title`)}
              showTitle={isScrolledPast && !isDesktopView}
            />
            <PageLayout.PageTitleContainer ref={titleRef}>
              <Text.H1>{t(`${translationKey}.title`)}</Text.H1>
            </PageLayout.PageTitleContainer>
            <Styled.Container>
              <Styled.Form>
                <Styled.FormGroup>
                  <Styled.EmojiDisplay>
                    <Text.H3>{getInitials(formData.full_name)}</Text.H3>
                  </Styled.EmojiDisplay>
                </Styled.FormGroup>

                <Styled.FormGroup>
                  <Styled.LabelWithTooltip>
                    <Text.P2Semibold>
                      {t(`${translationKey}.fullName`)}
                    </Text.P2Semibold>
                    {!isCreateMode && profile?.has_notes && (
                      <Tooltip
                        title={t('editDetails.nameLockedExplanation')}
                        open={isDesktopView ? undefined : showNameTooltip}
                        disableHoverListener={!isDesktopView}
                        placement="top"
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '0.875rem',
                              backgroundColor: '#424240',
                            },
                          },
                          arrow: {
                            sx: {
                              color: '#424240',
                            },
                          },
                        }}
                      >
                        <Styled.InfoIconWrapper
                          ref={tooltipRef}
                          role="button"
                          aria-label={t('editDetails.nameLockedExplanation')}
                          tabIndex={0}
                          onClick={
                            isDesktopView
                              ? undefined
                              : () => setShowNameTooltip(!showNameTooltip)
                          }
                          onKeyDown={handleTooltipKeyDown}
                        >
                          <General.InfoCircle />
                        </Styled.InfoIconWrapper>
                      </Tooltip>
                    )}
                  </Styled.LabelWithTooltip>
                  <Styled.StyledInput
                    type="text"
                    value={formData.full_name}
                    onChange={(e) =>
                      setFormData({ ...formData, full_name: e.target.value })
                    }
                    placeholder={
                      isCreateMode ? t('addProfile.enterFullName') : undefined
                    }
                    disabled={!isCreateMode && profile?.has_notes}
                  />
                </Styled.FormGroup>

                <Styled.FormGroup>
                  <Text.P2Semibold>
                    {t(`${translationKey}.role`)}
                  </Text.P2Semibold>
                  <Styled.StyledInput
                    type="text"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    placeholder={
                      isCreateMode ? t('addProfile.enterRole') : undefined
                    }
                  />
                </Styled.FormGroup>

                <Styled.FormGroup>
                  <Text.P2Semibold>
                    {t(`${translationKey}.nicknames`)}
                  </Text.P2Semibold>
                  <Styled.NicknamesContainer>
                    {formData.nicknames.map((nickname) => {
                      const isDeleted =
                        !isCreateMode && deletedNicknames.has(nickname);
                      return (
                        <Styled.NicknameTag
                          key={nickname}
                          $isDeleted={isDeleted}
                        >
                          <Text.P2>{nickname}</Text.P2>
                          {isDeleted ? (
                            <Styled.RemoveNicknameButton
                              onClick={() => handleRestoreNickname(nickname)}
                              aria-label={t('editDetails.restoreNickname')}
                            >
                              <Arrow.RefreshCcw01 size={16} />
                            </Styled.RemoveNicknameButton>
                          ) : (
                            <Styled.RemoveNicknameButton
                              onClick={() => handleRemoveNickname(nickname)}
                              aria-label={t(
                                isCreateMode
                                  ? 'addProfile.deleteNickname'
                                  : 'editDetails.deleteNickname'
                              )}
                            >
                              <General.XClose size={16} />
                            </Styled.RemoveNicknameButton>
                          )}
                        </Styled.NicknameTag>
                      );
                    })}
                    {isAddingNickname ? (
                      <EditableNicknameInput
                        isCreateMode={isCreateMode}
                        onSave={(val) => handleSaveNewNickname(val)}
                        onCancel={() => setIsAddingNickname(false)}
                      />
                    ) : (
                      <Styled.AddNicknameButton
                        onClick={() => setIsAddingNickname(true)}
                      >
                        <General.Plus size={16} />
                        <Text.P2>{t(`${translationKey}.addNickname`)}</Text.P2>
                      </Styled.AddNicknameButton>
                    )}
                  </Styled.NicknamesContainer>
                </Styled.FormGroup>

                <Styled.FormGroup>
                  <Text.P2Semibold>
                    {t(`${translationKey}.employmentNumber`)}
                  </Text.P2Semibold>
                  <Styled.StyledInput
                    type="text"
                    value={formData.employee_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        employee_id: e.target.value,
                      })
                    }
                    placeholder={
                      isCreateMode
                        ? t('addProfile.enterEmploymentNumber')
                        : undefined
                    }
                  />
                </Styled.FormGroup>

                <Styled.FormGroup>
                  <Text.P2Semibold>
                    {t(`${translationKey}.employmentStatus`)}
                  </Text.P2Semibold>
                  <SelectionDropdown
                    ariaLabel={t(`${translationKey}.employmentStatus`)}
                    value={formData.employment_status}
                    options={statusOptions}
                    placeholder={t('addProfile.selectStatus')}
                    fullWidth
                    onChange={(next) =>
                      setFormData({
                        ...formData,
                        employment_status: next as EmploymentStatus,
                      })
                    }
                  />
                </Styled.FormGroup>

                <Styled.FormGroup>
                  <Text.P2Semibold>
                    {t(`${translationKey}.departments`)}
                  </Text.P2Semibold>
                  <MultiSelectionDropdown
                    ariaLabel={t(`${translationKey}.departments`)}
                    value={formData.departments ?? []}
                    options={departmentOptions}
                    placeholder={t(`${translationKey}.selectDepartments`)}
                    maxWidth={null}
                    onChange={(next) =>
                      setFormData({ ...formData, departments: next })
                    }
                    renderValue={(selectedLabels) =>
                      selectedLabels.length > 0
                        ? selectedLabels.join(', ')
                        : t(`${translationKey}.selectDepartments`)
                    }
                  />
                </Styled.FormGroup>
              </Styled.Form>
            </Styled.Container>
          </PageLayout.Content>
        </PageLayout.ContentContainer>
      </ConditionalWrapper>

      <UnsavedChangesModal
        isOpen={showUnsavedChangesDialog}
        onSave={handleSaveAndNavigate}
        onDiscard={handleDiscardAndNavigate}
        onCancel={handleCancelNavigation}
      />
    </ErrorBoundary>
  );
}
